import React, { FC, useCallback, useEffect, useState } from 'react';
import root from 'react-shadow';
import Handlebars from 'handlebars';
import DOMPurify from 'dompurify';

import { useDeepCompareMemo } from '@clatter/platform';
import { listActionTypes } from '../../../../../../../../../constants';
import { registerHandlebarsHelpers } from 'apps/ng/src/app/helpers/handlebars.helpers';

import { PageLayoutBlockItem } from './PageLayoutBlockItem';
import { SectionWithColumns } from './SectionWithColumns';
import { Block } from 'apps/ng/src/app/services';

interface Props {
  onAction: any;
  data: any;
  allPageBlocks: HtmlTemplateBlock[];
  pageBlock?: HtmlTemplateBlock;
  layout: Block | null;
  context: Record<string, any>;
  isLoading?: boolean;
  blocks: Block[];
  isDirty: boolean;
  isExpanded: boolean;
  selectedColumn?: string | null;
  valuesSchema?: any;
}

const PreviewContainer = root['div'];

// Register Handlebars helpers once at module level
registerHandlebarsHelpers();

const hasColumns = (html: string): boolean => {
  return html.includes('{{column');
};

type ViewportSize = 'mobile' | 'tablet' | 'laptop' | '4k';

export const HtmlTemplatePreview: FC<Props> = ({
  onAction,
  isExpanded,
  data,
  allPageBlocks,
  pageBlock,
  blocks,
  layout,
  isDirty,
  context,
  selectedColumn: selectedColumnProp,
  valuesSchema,
}) => {
  const [selectedColumn, setSelectedColumn] = useState<string | null>(selectedColumnProp || null);
  const [viewportSize, setViewportSize] = useState<ViewportSize>('laptop');
  const [zoom, setZoom] = useState<number | 'fit'>('fit');
  const [calculatedFitZoom, setCalculatedFitZoom] = useState<number>(100);
  const shadowRootRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelectedColumn(selectedColumnProp || null);
  }, [selectedColumnProp]);

  useEffect(() => {
    const centerScroll = () => {
      if (shadowRootRef.current?.shadowRoot) {
        const wrapper = shadowRootRef.current.shadowRoot.querySelector(
          '.__clatter-preview__layout-wrapper',
        ) as HTMLDivElement;
        const container = shadowRootRef.current.shadowRoot.querySelector(
          '.__clatter-preview__layout-container',
        ) as HTMLDivElement;

        if (wrapper && container) {
          const viewportWidth = parseInt(getViewportWidth(viewportSize));
          const availableWidth = wrapper.clientWidth;
          const fitZoomValue = Math.floor((availableWidth / viewportWidth) * 100);
          setCalculatedFitZoom(Math.min(100, Math.max(25, fitZoomValue)));

          const scrollLeft = (container.offsetWidth - wrapper.clientWidth) / 2;
          wrapper.scrollLeft = Math.max(0, scrollLeft);
        }
      }
    };

    setTimeout(centerScroll, 100);
  }, [viewportSize]);

  const handleShadowRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      shadowRootRef.current = node;

      if (node?.shadowRoot) {
        setTimeout(() => {
          const wrapper = node.shadowRoot!.querySelector('.__clatter-preview__layout-wrapper') as HTMLDivElement;
          const container = node.shadowRoot!.querySelector('.__clatter-preview__layout-container') as HTMLDivElement;

          if (wrapper && container) {
            const viewportWidth = parseInt(getViewportWidth(viewportSize));
            const availableWidth = wrapper.clientWidth;
            const fitZoomValue = Math.floor((availableWidth / viewportWidth) * 100);
            setCalculatedFitZoom(Math.min(100, Math.max(25, fitZoomValue)));

            const scrollLeft = (container.offsetWidth - wrapper.clientWidth) / 2;
            wrapper.scrollLeft = Math.max(0, scrollLeft);
          }
        }, 100);
      }
    },
    [viewportSize],
  );

  const usedBlocks: any[] = (pageBlock?.allBlocksUsed || [])
    .map((used: any) => blocks.find((b) => b.name === used.name))
    .filter(Boolean);

  const styles = usedBlocks.filter((b) => b.blockStylesheet?.css).map((b) => b.blockStylesheet.css);

  if (layout?.blockStylesheet?.css) {
    const cssToShadow = layout.blockStylesheet.css.replace(/:root\b/g, ':host');
    styles.push(cssToShadow);
  }

  const compiledLayoutParts = useDeepCompareMemo(() => {
    const [firstPart = '', secondPart = ''] = (layout?.html || '').split('{{> @partial-block }}');

    usedBlocks.forEach((block) => {
      if (!block) return console.error('Error registering block data not found!', block);
      Handlebars.registerPartial(block.name, block.html || '');
    });

    let firstPartHtml = '';
    let secondPartHtml = '';

    try {
      const system = {
        System: {
          BasePath: `/`,
          Pages: allPageBlocks.map((b) => ({
            DisplayName: b?.displayName ?? 'Preview',
            name: b?.name ?? 'preview',
          })),
          Page: {
            DisplayName: pageBlock?.displayName ?? 'Preview',
            name: pageBlock?.name ?? 'preview',
          },
          Sections: [{ DisplayName: 'Section #1' }, { DisplayName: 'Section #2' }, { DisplayName: 'Section #3' }],
          CurrentYear: new Date().getFullYear(),
        },
      };

      const compiledFirst = Handlebars.compile(firstPart);
      const compiledSecond = Handlebars.compile(secondPart);
      firstPartHtml = DOMPurify.sanitize(compiledFirst(context || {}, { data: system }));
      secondPartHtml = DOMPurify.sanitize(compiledSecond(context || {}, { data: system }));
    } catch (error) {
      console.error('Error compiling Handlebars layout template:', error);
    }

    return { firstPartHtml, secondPartHtml };
  }, [pageBlock, layout, usedBlocks, context, allPageBlocks]);

  const getDisabledActions = useCallback(
    (index: number) => {
      const actions = [];
      if (index === 0) {
        actions.push(listActionTypes.MOVE_UP);
      }
      if (index === (data?.value || []).length - 1) {
        actions.push(listActionTypes.MOVE_DOWN);
      }
      if (isDirty) {
        actions.push(listActionTypes.OPEN_SETTINGS);
      }
      return actions;
    },
    [data, isDirty],
  );

  const handleColumnSelect = (columnPath: string) => {
    setSelectedColumn(columnPath);
    onAction({ type: 'SELECT_COLUMN', payload: columnPath });
  };

  const currentZoomLevel = zoom === 'fit' ? calculatedFitZoom : zoom;
  const wrapperMaxWidth = isExpanded ? 1270 : 1000;

  const getViewportWidth = (size: ViewportSize): string => {
    switch (size) {
      case 'mobile':
        return '425px';
      case 'tablet':
        return '768px';
      case 'laptop':
        return '1440px';
      case '4k':
        return '2560px';
      default:
        return '1440px';
    }
  };

  const getViewportMinWidth = (size: ViewportSize): string => {
    switch (size) {
      case 'mobile':
        return '320px';
      case 'tablet':
        return '768px';
      case 'laptop':
        return '1440px';
      case '4k':
        return '2560px';
      default:
        return '1440px';
    }
  };

  return (
    <PreviewContainer ref={handleShadowRootRef}>
      {/* Base styles and CSS reset for shadow DOM */}
      <style>{`
        /* CSS Reset for shadow DOM */
        *, *::before, *::after {
          box-sizing: border-box;
        }

        :host {
          /* Base font settings that match typical browser defaults */
          all: initial;
          display: block;
          font-family: sans-serif;
          font-size: 16px;
          line-height: 1.5;
          color: #000;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

         .shadow-wrapper {
            width: 100%;
            max-width: ${isExpanded ? '1270px' : '1000px'};
            margin: 0 auto;
            overflow: hidden;
          }
      `}</style>

      {styles.map((style, index) => (
        <style key={index} type="text/css">
          {style}
        </style>
      ))}

      <style>{`
        /* Preview-specific styles with unique prefix to avoid conflicts with block CSS */
        .__clatter-preview__viewport-controls {
          display: flex;
          gap: 8px;
          padding: 8px;
          background: #f5f5f5;
          border-bottom: 1px solid #e0e0e0;
          justify-content: center;
          align-items: center;
        }

        .__clatter-preview__expand-control {
         display: flex;
          align-items: center;
          gap: 8px;
          margin-right: 16px;
          padding-right: 16px;
          border-right: 1px solid #ccc;
        }

        .__clatter-preview__zoom-control {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: 16px;
          padding-left: 16px;
          border-left: 1px solid #ccc;
        }

        .__clatter-preview__zoom-label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }

        .__clatter-preview__zoom-select {
          padding: 4px 8px;
          border: 1px solid #ccc;
          background: white;
          cursor: pointer;
          border-radius: 4px;
          font-size: 12px;
          color: #666;
        }

        .__clatter-preview__zoom-select:hover {
          background: #f0f0f0;
        }

        .__clatter-preview__viewport-btn {
          padding: 6px 16px;
          border: 1px solid #ccc;
          background: white;
          cursor: pointer;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          color: #666;
          transition: all 0.2s;
        }

        .__clatter-preview__viewport-btn:hover {
          background: #f0f0f0;
          color: #000;
        }

        .__clatter-preview__viewport-btn.__clatter-preview__active {
          background: #2196F3;
          color: white;
          border-color: #2196F3;
        }

        .__clatter-preview__layout-wrapper {
          width: 100%;
          height: calc(100% - 40px);
          overflow: auto;
          background: #f8f9fa;
          position: relative;
          scroll-behavior: smooth;
        }

        .__clatter-preview__layout-container {
          position: relative;
          overflow: visible;
          width: ${getViewportWidth(viewportSize)};
          min-width: ${getViewportMinWidth(viewportSize)};
          max-width: ${getViewportWidth(viewportSize)};
          height: fit-content;
          min-height: 100%;
          border-left: 1px solid lightgray;
          border-right: 1px solid lightgray;
          border-bottom: 1px solid lightgray;
          padding: 3px;
          line-height: 1.6;
          background: white;
          margin: 0 auto;
          flex-shrink: 0;
          transform: scale(${zoom === 'fit' ? calculatedFitZoom / 100 : zoom / 100});
          transform-origin: top center;
        }

        .__clatter-preview__section-with-columns {
          margin-bottom: 16px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          background: white;
          overflow: hidden;
        }

        .__clatter-preview__section-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #f5f5f5;
          border-bottom: 1px solid #e0e0e0;
        }

        .__clatter-preview__section-expand-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          font-size: 12px;
          color: #666;
        }

        .__clatter-preview__section-expand-btn:hover {
          color: #000;
        }

        .__clatter-preview__section-title {
          flex: 1;
          font-weight: 600;
          font-size: 14px;
          color: #333;
        }

        .__clatter-preview__section-controls {
          display: flex;
          gap: 4px;
        }

        .__clatter-preview__section-controls button {
          padding: 4px 8px;
          border: 1px solid #ccc;
          background: white;
          cursor: pointer;
          border-radius: 4px;
          font-size: 12px;
          color: #666;
        }

        .__clatter-preview__section-controls button:hover:not(:disabled) {
          background: #f0f0f0;
          color: #000;
        }

        .__clatter-preview__section-controls button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .__clatter-preview__section-body {
          padding: 16px;
        }

        .__clatter-preview__column-slot {
          min-height: 120px;
          border: 2px dashed transparent;
          border-radius: 4px;
          padding: 8px;
          transition: all 0.2s;
          cursor: pointer;
          position: relative;
        }

        .__clatter-preview__column-slot:hover {
          border-color: #2196F3;
          background: rgba(33, 150, 243, 0.05);
        }

        .__clatter-preview__column-slot.__clatter-preview__column-selected {
          border-color: #1976D2;
          border-style: solid;
          background: rgba(25, 118, 210, 0.08);
        }

        .__clatter-preview__column-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100px;
          color: #999;
          font-size: 13px;
          gap: 8px;
          text-align: center;
          padding: 8px;
        }

        .__clatter-preview__column-block-item {
          position: relative;
          margin-bottom: 8px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 8px;
          background: white;
        }

        .__clatter-preview__column-block-item:hover .__clatter-preview__column-block-controls {
          opacity: 1;
        }

        .__clatter-preview__column-block-controls {
          position: absolute;
          top: 4px;
          right: 4px;
          display: flex;
          gap: 2px;
          opacity: 0;
          transition: opacity 0.2s;
          background: white;
          padding: 2px;
          border-radius: 3px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .__clatter-preview__column-btn-sm {
          padding: 2px 6px;
          border: 1px solid #ccc;
          background: white;
          cursor: pointer;
          border-radius: 3px;
          font-size: 11px;
        }

        .__clatter-preview__column-btn-sm:hover:not(:disabled) {
          background: #f0f0f0;
        }

        .__clatter-preview__column-btn-sm:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
      `}</style>

      <div className="shadow-wrapper">
        {/* Viewport size controls */}
        <div className="__clatter-preview__viewport-controls">
          <div className="__clatter-preview__expand-control">
            {isExpanded ? (
              <button onClick={() => onAction({ type: 'COLLAPSE' })}>
                <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </button>
            ) : (
              <button onClick={() => onAction({ type: 'EXPAND' })}>
                <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </button>
            )}
          </div>

          <button
            className={`__clatter-preview__viewport-btn ${
              viewportSize === 'mobile' ? '__clatter-preview__active' : ''
            }`}
            onClick={() => setViewportSize('mobile')}
          >
            Mobile (425px)
          </button>
          <button
            className={`__clatter-preview__viewport-btn ${
              viewportSize === 'tablet' ? '__clatter-preview__active' : ''
            }`}
            onClick={() => setViewportSize('tablet')}
          >
            Tablet (768px)
          </button>
          <button
            className={`__clatter-preview__viewport-btn ${
              viewportSize === 'laptop' ? '__clatter-preview__active' : ''
            }`}
            onClick={() => setViewportSize('laptop')}
          >
            Laptop (1440px)
          </button>
          <button
            className={`__clatter-preview__viewport-btn ${viewportSize === '4k' ? '__clatter-preview__active' : ''}`}
            onClick={() => setViewportSize('4k')}
          >
            4K (2560px)
          </button>

          {/* Zoom control */}
          <div className="__clatter-preview__zoom-control">
            <span className="__clatter-preview__zoom-label">Zoom</span>
            <select
              className="__clatter-preview__zoom-select"
              value={zoom}
              onChange={(e) => {
                const value = e.target.value;
                setZoom(value === 'fit' ? 'fit' : Number(value));
              }}
            >
              <option value="fit">Fit ({calculatedFitZoom}%)</option>
              <option value={50}>50%</option>
              <option value={75}>75%</option>
              <option value={100}>100%</option>
              <option value={125}>125%</option>
              <option value={150}>150%</option>
            </select>
          </div>
        </div>

        {/* Layout wrapper for centering */}
        <div className="__clatter-preview__layout-wrapper">
          <div className="__clatter-preview__layout-container">
            <div style={{ zIndex: '1' }} dangerouslySetInnerHTML={{ __html: compiledLayoutParts.firstPartHtml }} />

            {data?.value?.length > 0 ? (
              data.value.map((block: Block & { instanceId: string }, index: number) => {
                if (hasColumns(block.html || '')) {
                  return (
                    <SectionWithColumns
                      key={block.instanceId}
                      block={block}
                      context={context}
                      selectedColumn={selectedColumn}
                      onColumnSelect={handleColumnSelect}
                      onAction={onAction}
                      disabledActions={getDisabledActions(index)}
                      zoomLevel={currentZoomLevel}
                      viewportWidth={wrapperMaxWidth}
                    />
                  );
                }

                // Create scoped context for this block instance
                const getBlockKey = (blockName: string, instanceId?: string) => {
                  return instanceId ? `${blockName}_${instanceId}` : blockName;
                };

                // Always use unique keys, with fallback to simple name for backwards compatibility
                const blockKey = getBlockKey(block.name, block.instanceId);
                const blockContext = context[blockKey] || context[block.name] || {};

                // Get block schema for displayName
                const blockSchema =
                  (pageBlock && valuesSchema?.properties?.[pageBlock.name]?.properties?.[blockKey]) ||
                  (pageBlock && valuesSchema?.properties?.[pageBlock.name]?.properties?.[block.name]);

                return (
                  <PageLayoutBlockItem
                    key={block.instanceId}
                    block={block}
                    context={blockContext}
                    disabledActions={getDisabledActions(index)}
                    onAction={onAction}
                    blockSchema={blockSchema}
                    zoomLevel={currentZoomLevel}
                    viewportWidth={wrapperMaxWidth}
                  />
                );
              })
            ) : (
              <div
                style={{
                  minHeight: '300px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  outline: '2px solid orange',
                  zIndex: '10',
                }}
              >
                <p>{!layout ? ' Please Select a Layout ' : 'Please add blocks to the layout'}</p>
              </div>
            )}

            <div style={{ zIndex: '1' }} dangerouslySetInnerHTML={{ __html: compiledLayoutParts.secondPartHtml }} />
          </div>
        </div>
      </div>
    </PreviewContainer>
  );
};
